import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import IntegritasLogoS from '../../assets/logos/IntegritasLogoS';
import { useAuth } from '../../context/AuthContext';
import MobileMenu from './MobileMenu';

const Header = () => {
  const navigate = useNavigate(); // Hook for programmatic navigation
  const location = useLocation(); // Get the current route location
  const { logout, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHistoryDropdownOpen, setIsHistoryDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsHistoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  /**
   * Handles navigation to a specified URL when a button is clicked.
   * @param {string} url - The target URL to navigate to.
   * @param {React.MouseEvent<HTMLButtonElement>} e - The mouse event triggered by the click.
   */
  const handleNavigate = (url: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(url);
  };

  /**
   * Checks if the given path matches the current location's pathname.
   * @param {string} path - The route path to compare.
   * @returns {boolean} True if the given path is the active route.
   */
  const isActive = (path: string) => location.pathname === path; //TODO: White underline for active page / change color

  const isHistoryActive = () => isActive('/stamps') || isActive('/verifications');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      <header
        className={`h-[100px] px-4 md:px-[60px] bg-black flex justify-between items-center relative z-[9999] ${
          isMobileMenuOpen ? 'md:flex hidden' : 'flex'
        }`}
      >
        <div className="flex items-center">
          <Link to={`/`} className="flex cursor-pointer">
            <IntegritasLogoS />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
          <ul className="flex gap-10 text-sm font-normal">
            {/* HOME */}
            <li className="flex items-center">
              <button
                onClick={(e) => handleNavigate('/', e)}
                className={`${
                  isActive('/') ? 'text-minima-orange-2023' : 'hover:text-core-grey-80'
                }`}
              >
                Stamp
              </button>
            </li>
            <li className="flex items-center">
              <button
                onClick={(e) => handleNavigate('/analyze', e)}
                className={`${
                  isActive('/analyze') ? 'text-minima-orange-2023' : 'hover:text-core-grey-80'
                }`}
              >
                Analyze
              </button>
            </li>
            <li className="flex items-center relative" ref={dropdownRef}>
              <button
                onClick={() => setIsHistoryDropdownOpen(!isHistoryDropdownOpen)}
                className={`${
                  isHistoryActive() ? 'text-minima-orange-2023' : 'hover:text-core-grey-80'
                } flex items-center gap-1`}
              >
                History
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isHistoryDropdownOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isHistoryDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 bg-black border border-core-black-contrast-2 rounded-md shadow-lg z-[99999] min-w-[160px]">
                  <button
                    onClick={(e) => {
                      handleNavigate('/stamps', e);
                      setIsHistoryDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-core-black-contrast transition-colors ${
                      isActive('/stamps') ? 'text-minima-orange-2023' : 'text-white'
                    }`}
                  >
                    Stamps
                  </button>
                  <button
                    onClick={(e) => {
                      handleNavigate('/verifications', e);
                      setIsHistoryDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-core-black-contrast transition-colors ${
                      isActive('/verifications') ? 'text-minima-orange-2023' : 'text-white'
                    }`}
                  >
                    Verifications
                  </button>
                </div>
              )}
            </li>
            <li className="flex items-center">
              <button
                onClick={(e) => handleNavigate('/about', e)}
                className={`${
                  isActive('/about') ? 'text-minima-orange-2023' : 'hover:text-core-grey-80'
                }`}
              >
                About
              </button>
            </li>
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-white p-2"
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Desktop Logout */}
        {isAuthenticated && (
          <div className="hidden md:flex items-center">
            <button
              onClick={handleLogout}
              className="text-sm hover:text-minima-orange-2023 transition-colors"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>
  );
};

export default Header;
